import * as React from "react";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { useUploadConsentDocuments } from "../../hooks/mutations/reports.mutations";
import useToast from "../../../../hooks/useToast";
import * as FilePicker from "../../../../shared-ui/FilePicker/FilePicker";

interface UploadDocumentModalProps {
    consentId: string;
    onFormSubmit: () => void;
}

export default function UploadDocumentForm({
    consentId,
    onFormSubmit,
}: UploadDocumentModalProps) {
    const [selectedFiles, setSelectedFiles] = React.useState<File[] | null>(
        null
    );

    const { toast } = useToast();
    const uploadDocumentsMutation = useUploadConsentDocuments();

    const queryClient = useQueryClient();

    const isUploadDocumentMutating = useIsMutating([
        "upload-consent-documents",
    ]);

    const onDrop = React.useCallback(
        (acceptedFiles: File[]) => {
            if (selectedFiles && selectedFiles.length > 0) {
                setSelectedFiles([...selectedFiles, ...acceptedFiles]);
            } else {
                setSelectedFiles([...acceptedFiles]);
            }
        },
        [selectedFiles]
    );

    const handleRemoveSelectedFile = (filename: string) => {
        if (selectedFiles) {
            const filesArr = [...selectedFiles].filter(
                (file) => file.name !== filename
            );
            if (filesArr.length === 0) setSelectedFiles(null);
            else setSelectedFiles(filesArr);
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!selectedFiles || (selectedFiles && selectedFiles.length === 0)) {
            toast({
                mode: "error",
                message: "Please select  file(s) to upload",
            });
        } else {
            const formData = new FormData();
            for (let i = 0; i < selectedFiles.length; i += 1) {
                formData.append("documents", selectedFiles[i]);
            }

            uploadDocumentsMutation.mutate(
                {
                    consentId,
                    formData,
                },
                {
                    onSuccess: (res) => {
                        toast({
                            mode: "success",
                            message:
                                res.message ||
                                `${selectedFiles.length} document${
                                    selectedFiles.length > 1 ? "s" : ""
                                } uploaded successfully`,
                        });
                        queryClient.invalidateQueries({
                            queryKey: ["consent-reports"],
                        });
                        queryClient.invalidateQueries({
                            queryKey: ["expiring-consents"],
                        });

                        setSelectedFiles(null);
                        onFormSubmit();
                    },
                    onError: (error) => {
                        toast({
                            mode: "error",
                            message:
                                error?.response?.data?.message ||
                                "Could not upload documents at this time",
                        });
                    },
                }
            );
        }
    };

    return (
        <form
            className="space-y-4"
            onSubmit={handleSubmit}
            id="upload-consent-documents"
        >
            <FilePicker.FilePicker
                onDrop={onDrop}
                disabled={isUploadDocumentMutating > 0}
            >
                <FilePicker.FilePickerIcon />
                <FilePicker.FilePickerMeta description="Click to upload documents" />
            </FilePicker.FilePicker>

            {selectedFiles && selectedFiles.length > 0 && (
                <div>
                    <FilePicker.FilePickerSelectedFiles
                        selectedFiles={selectedFiles}
                        handleRemoveSelectedFile={handleRemoveSelectedFile}
                        wrapperClassName="grid grid-cols-1 grid-flow-row flex-row gap-[--spacing-items]"
                    />
                </div>
            )}
        </form>
    );
}
